import React, { useEffect, useState, useMemo } from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import NavBar from 'Components/Organisms/NavBar';
import FeatureNavigation from 'Components/Molecules/FeatureNavigation';
import { paths } from 'App/ReservationBook/shared';
import Container from 'Components/Atoms/Container';
import useCollection from 'CustomHooks/useCollection';
import { RegularTable } from 'types/reservations';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import IconButton from 'Components/Atoms/IconButton';
import { Add, Edit, Visibility } from '@material-ui/icons';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import RegularTableDetailSidebar from './Components/RegularTableDetailSidebar';
import AddEditRegularTableModal from './Components/AddEditRegularTableModal';
import { dateHelper, toEuropeanDate } from 'utils/helper';

export type RegularTablesListProps = {
  restaurantId: string;
};

const formatRepeatingPattern = (rule: any): string => {
  if (!rule) return 'Keine Wiederholung';

  switch (rule.type) {
    // New format
    case 'weekly': {
      const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
      const days = (rule.weekDays || rule.days || [])
        .map((d: number) => dayNames[d])
        .join(', ');
      return days ? `Wöchentlich: ${days}` : 'Wöchentlich';
    }
    // Old format
    case 'monthlySpecificDay':
    case 'monthly_fix_day': {
      return `Monatlich: ${rule.day || 1}. Tag`;
    }
    // Old format
    case 'monthlyDayOfWeek':
    case 'monthly': {
      const week = rule.option ?? rule.week;
      const weekStr = week === -1 ? 'Letzter' : `${week || 1}.`;
      const dayNames = [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ];
      // Old format uses `days` array, new uses `weekDay`
      const dayIndex = rule.weekDay ?? (rule.days?.[0] || 0);
      return `Monatlich: ${weekStr} ${dayNames[dayIndex]}`;
    }
    // Old format
    case 'yearlySpecificDate':
    case 'yearly': {
      return `Jährlich: ${rule.date || '01.01'}`;
    }
    default:
      return 'Unbekannt';
  }
};

const RegularTablesList: React.FC<RegularTablesListProps> = ({
  restaurantId,
}) => {
  const [limit, setLimit] = useState(100);
  const [selectedRegularTable, setSelectedRegularTable] =
    useState<RegularTable | null>(null);
  const [showDetailSidebar, setShowDetailSidebar] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingTable, setEditingTable] = useState<RegularTable | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');

  const [regularTables, setRegularTables] = useCollection<RegularTable>(
    'regularTables',
    {
      filter: [['restaurant', '==', restaurantId]],
      // Note: Sorting by createdAt requires a composite index in Firebase
      // Create it here: Firebase Console > Firestore > Indexes
      // Temporarily disabled to show data immediately:
      // sort: ["createdAt", "desc"],
      limit,
      keepDataWhileLoading: true,
    }
  );

  // Debug: Log the data
  useEffect(() => {
    console.log('🔍 RegularTables data:', {
      loading: regularTables.loading,
      dataLength: regularTables.data.length,
      data: regularTables.data,
      restaurantId,
    });
  }, [regularTables.data, regularTables.loading, restaurantId]);

  const columns = useMemo<ColumnDef<RegularTable & { id: string }>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => (row.active ? 'Aktiv' : 'Inaktiv'),
        cell: (info) => (
          <Box
            className={`status-badge ${
              info.getValue() === 'Aktiv' ? 'active' : 'inactive'
            }`}
          >
            {info.getValue() as string}
          </Box>
        ),
        size: 100,
      },
      {
        id: 'guest',
        header: 'Gast',
        accessorFn: (row) => {
          const guest = row.guest || (row as any).reservation?.guest;
          return guest?.name || guest?.email || 'N/A';
        },
        cell: (info) => {
          const guest =
            info.row.original.guest ||
            (info.row.original as any).reservation?.guest;
          return (
            <Box>
              <Typography variant="text-2" weight="medium">
                {info.getValue() as string}
              </Typography>
              <Typography variant="text-4" color="subdued">
                {guest?.email || ''}
              </Typography>
            </Box>
          );
        },
        size: 200,
      },
      {
        id: 'pattern',
        header: 'Wiederholung',
        accessorFn: (row) => {
          const rule = row.repeating || (row as any).rule;
          return rule ? formatRepeatingPattern(rule) : 'N/A';
        },
        cell: (info) => (
          <Typography variant="text-3">{info.getValue() as string}</Typography>
        ),
        size: 200,
      },
      {
        id: 'time',
        header: 'Zeit',
        accessorFn: (row) =>
          row.time || (row as any).reservation?.time || 'N/A',
        cell: (info) => (
          <Typography variant="text-3">
            {info.getValue() as string}
          </Typography>
        ),
        size: 100,
      },
      {
        id: 'guests',
        header: 'Gäste',
        accessorFn: (row) =>
          row.guests || (row as any).reservation?.guests || 2,
        cell: (info) => (
          <Typography variant="text-3">
            {info.getValue() as number} Personen
          </Typography>
        ),
        size: 100,
      },
      {
        id: 'tables',
        header: 'Tische',
        accessorFn: (row) => {
          const tables = row.tables || (row as any).reservation?.tables;
          return tables && tables.length > 0 ? tables.join(', ') : 'N/A';
        },
        cell: (info) => (
          <Typography variant="text-3">{info.getValue() as string}</Typography>
        ),
        size: 150,
      },
      {
        id: 'startDate',
        header: 'Start',
        accessorFn: (row) =>
          row.firstReservationAt || (row as any).reservation?.date || '',
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            <Typography variant="text-3">
              {value ? toEuropeanDate(value) : '-'}
            </Typography>
          );
        },
        size: 120,
      },
      {
        id: 'endDate',
        header: 'Ende',
        accessorFn: (row) =>
          row.lastReservation || (row as any).endDate || '-',
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            <Typography variant="text-3">
              {value === '-' || !value ? '-' : toEuropeanDate(value)}
            </Typography>
          );
        },
        size: 120,
      },
      {
        id: 'actions',
        header: 'Aktionen',
        cell: (info) => (
          <Box flex style={{ gap: '0.5rem' }}>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedRegularTable(info.row.original);
                setShowDetailSidebar(true);
              }}
              title="Details anzeigen"
            >
              <Visibility fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setEditingTable(info.row.original);
                setShowAddEditModal(true);
              }}
              title="Bearbeiten"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Box>
        ),
        size: 120,
      },
    ],
    []
  );

  const table = useReactTable({
    data: regularTables.data || [],
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <Box background id="reservation-stat-screen">
      <NavBar
        title="Stammtische"
        className="fixed-nav"
        mainProduct={{
          label: 'Reservations',
          link: (rId) => `/${rId}/reservations/${dateHelper()}/tablePlan`,
        }}
        reservationBook
        right={
          <Button
            variant="primary"
            translation="reservations"
            onClick={() => {
              setEditingTable(null);
              setShowAddEditModal(true);
            }}
            endIcon={(p) => <Add {...p} />}
          >
            Neuer Stammtisch
          </Button>
        }
        childrenContainerStyle={{
          width: '270px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transform: 'translateX(-100px)',
        }}
      ></NavBar>
      <FeatureNavigation
        date={dateHelper()}
        paths={paths}
        current="regularTables"
        className="featureNavigation"
      />
      <Container variant="reservations">
        <Box>
          <Box>
            <Typography
              style={{ display: 'block', marginBottom: 8 }}
              variant="h3"
            >
              Stammtische
            </Typography>
            {regularTables.loading ? (
              <Box style={{ padding: '2rem', textAlign: 'center' }}>
                <Typography>Lade Stammtische...</Typography>
              </Box>
            ) : regularTables.data.length === 0 ? (
              <Box
                style={{
                  padding: '3rem',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h3" style={{ marginBottom: '1rem' }}>
                  Keine Stammtische vorhanden
                </Typography>
                <Typography
                  variant="text-2"
                  color="subdued"
                  style={{ marginBottom: '2rem' }}
                >
                  Erstellen Sie Ihren ersten Stammtisch für wiederkehrende
                  Reservierungen
                </Typography>
                <Button
                  endIcon={(p) => <Add {...p} />}
                  onClick={() => {
                    setEditingTable(null);
                    setShowAddEditModal(true);
                  }}
                >
                  Ersten Stammtisch erstellen
                </Button>
              </Box>
            ) : (
              <>
                <Box className="regularTablesTable">
                  <table>
                    <thead>
                      {table.getHeaderGroups()?.map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            return (
                              <th key={header.id} colSpan={header.colSpan}>
                                {header.isPlaceholder ? null : (
                                  <div>
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      )) ?? <></>}
                    </thead>
                    <tbody>
                      {table.getRowModel()?.rows?.map((row) => {
                        return (
                          <tr
                            key={row.id}
                            onDoubleClick={() => {
                              setSelectedRegularTable(row.original);
                              setShowDetailSidebar(true);
                            }}
                          >
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <td key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }) ?? <></>}
                    </tbody>
                  </table>
                  <div className="pagination">
                    <button
                      className="border rounded p-1"
                      onClick={() => table.setPageIndex(0)}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {'<<'}
                    </button>
                    <button
                      className="border rounded p-1"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {'<'}
                    </button>
                    <button
                      className="border rounded p-1"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      {'>'}
                    </button>
                    <button
                      className="border rounded p-1"
                      onClick={() =>
                        table.setPageIndex(table.getPageCount() - 1)
                      }
                      disabled={!table.getCanNextPage()}
                    >
                      {'>>'}
                    </button>
                    <span className="flex items-center gap-1">
                      <div>Seite</div>
                      <strong>
                        {table.getState().pagination.pageIndex + 1} von{' '}
                        {table.getPageCount()}
                      </strong>
                    </span>
                    <span className="flex items-center gap-1">
                      | Gehe zu:
                      <input
                        type="number"
                        defaultValue={table.getState().pagination.pageIndex + 1}
                        onChange={(e) => {
                          const page = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          table.setPageIndex(page);
                        }}
                        style={{ width: '50px' }}
                      />
                    </span>
                    <select
                      value={table.getState().pagination.pageSize}
                      onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                      }}
                    >
                      {[10, 20, 30, 40, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          Zeige {pageSize}
                        </option>
                      ))}
                    </select>
                  </div>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Container>

      {showDetailSidebar && selectedRegularTable && (
        <RegularTableDetailSidebar
          regularTable={selectedRegularTable}
          restaurantId={restaurantId}
          onClose={() => {
            setShowDetailSidebar(false);
            setSelectedRegularTable(null);
          }}
          onEdit={(table: RegularTable) => {
            setEditingTable(table);
            setShowAddEditModal(true);
            setShowDetailSidebar(false);
          }}
        />
      )}

      {showAddEditModal && (
        <AddEditRegularTableModal
          regularTable={editingTable}
          restaurantId={restaurantId}
          onClose={() => {
            setShowAddEditModal(false);
            setEditingTable(null);
          }}
          onSave={() => {
            setShowAddEditModal(false);
            setEditingTable(null);
            // Refresh list if needed
          }}
        />
      )}
    </Box>
  );
};

export default RegularTablesList;
